import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import {
  Badge,
  Button,
  Col,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";
import { mediaUrl } from "../../helpers/mediaHelper";
import {
  ADMIN_FIT_NOTE_REJECTION_STATUS,
  ADMIN_FIT_NOTE_REJECTION_STATUS_LABELS,
  FIT_NOTE_STATUS_LABELS,
  LEAVE_REASONS_LABELS,
  LEAVE_STATUS,
  LEAVE_STATUS_LABELS,
  LEAVE_TYPE,
  LEAVE_TYPE_LABELS,
  SICKNESS_MARK_TYPE_LABELS,
} from "../../constants/booking";
import {
  canAdminDecideLeaveRequest,
  formatAdminLeaveDates,
  getAdminLeaveReason,
  getAdminLeaveReasonDescription,
  getAdminLeaveShiftCount,
} from "./adminLeaveRequestHelpers";

const STATUS_COLOR_MAP = {
  [LEAVE_STATUS.PENDING]: "warning",
  [LEAVE_STATUS.APPROVED]: "success",
  [LEAVE_STATUS.REJECTED]: "danger",
};

function DetailField({ label, children }) {
  return (
    <Col sm={6} className="mb-3">
      <p className="text-muted small mb-1">{label}</p>
      <div className="mb-0">{children ?? "—"}</div>
    </Col>
  );
}

function LeaveDaysSection({ leaveDays }) {
  if (!Array.isArray(leaveDays) || leaveDays.length === 0) {
    return <p className="text-muted mb-0">—</p>;
  }

  return (
    <div className="d-flex flex-column gap-2">
      {leaveDays.map((day) => (
        <div key={day.date} className="border rounded p-2 bg-light">
          <div className="fw-medium mb-1">
            {dayjs(day.date).format("DD MMM YYYY")}
          </div>
          {Array.isArray(day.shiftLeaves) && day.shiftLeaves.length > 0 ? (
            day.shiftLeaves.map((shiftLeave, index) => (
              <div key={`${day.date}-${index}`} className="small text-muted">
                {shiftLeave.reason && (
                  <span>
                    {LEAVE_REASONS_LABELS[shiftLeave.reason] || shiftLeave.reason}
                    {shiftLeave.reasonDescription
                      ? ` — ${shiftLeave.reasonDescription}`
                      : ""}
                  </span>
                )}
                {shiftLeave.bookingWorkerRequestId && (
                  <span className="d-block">
                    Booking request: {shiftLeave.bookingWorkerRequestId}
                  </span>
                )}
              </div>
            ))
          ) : (
            <span className="small text-muted">No shift leaves</span>
          )}
        </div>
      ))}
    </div>
  );
}

export default function LeaveRequestDetailModal({
  isOpen,
  toggle,
  request,
  onApprove,
  onReject,
  isSubmitting,
}) {
  const [rejectMode, setRejectMode] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [fitNoteStatus, setFitNoteStatus] = useState("");

  useEffect(() => {
    if (!isOpen) {
      setRejectMode(false);
      setRejectionReason("");
      setFitNoteStatus("");
    }
  }, [isOpen, request?.slug]);

  if (!request) return null;

  const canDecide = canAdminDecideLeaveRequest(request);
  const statusColor = STATUS_COLOR_MAP[request.status] || "secondary";
  const reason = getAdminLeaveReason(request);
  const reasonDescription = getAdminLeaveReasonDescription(request);
  const fitNoteUrl = request.fitNote ? mediaUrl(request.fitNote) : "";

  const handleApprove = () => {
    onApprove?.(request);
  };

  const handleConfirmReject = () => {
    onReject?.(request, {
      rejectionReason: rejectionReason.trim(),
      fitNoteStatus: request.fitNote ? fitNoteStatus : undefined,
    });
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" centered>
      <ModalHeader toggle={toggle}>Leave request details</ModalHeader>
      <ModalBody>
        <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
          <div>
            <h5 className="mb-1">{request.slug || "—"}</h5>
            <p className="text-muted small mb-0">
              Submitted{" "}
              {request.createdAt
                ? dayjs(request.createdAt).format("DD MMM YYYY, HH:mm")
                : "—"}
            </p>
          </div>
          <Badge color={statusColor} className="text-uppercase">
            {LEAVE_STATUS_LABELS[request.status] || request.status}
          </Badge>
        </div>

        <Row>
          <DetailField label="Worker">
            <div className="fw-medium">{request.worker?.user?.fullName || "—"}</div>
            <small className="text-muted">{request.worker?.user?.email}</small>
          </DetailField>
          <DetailField label="Phone">
            {request.worker?.user?.phoneNumber || "—"}
          </DetailField>
          <DetailField label="Type">
            {LEAVE_TYPE_LABELS[request.type] || request.type || "—"}
          </DetailField>
          <DetailField label="Dates">
            {formatAdminLeaveDates(request.leaveDays)}
          </DetailField>
          <DetailField label="Shifts">
            {getAdminLeaveShiftCount(request.leaveDays) || "—"}
          </DetailField>
          {request.type === LEAVE_TYPE.SICK && (
            <DetailField label="Sick mark">
              {request.sickMarkType ? (
                <Badge color="info" className="text-capitalize">
                  {SICKNESS_MARK_TYPE_LABELS[request.sickMarkType] ||
                    request.sickMarkType}
                </Badge>
              ) : (
                "—"
              )}
            </DetailField>
          )}
          <DetailField label="Reason">
            {reason ? LEAVE_REASONS_LABELS[reason] || reason : "—"}
          </DetailField>
          <DetailField label="Description">
            <span className="text-break">{reasonDescription || "—"}</span>
          </DetailField>
        </Row>

        <div className="mb-3">
          <p className="text-muted small mb-2">Leave days</p>
          <LeaveDaysSection leaveDays={request.leaveDays} />
        </div>

        {request.fitNote && (
          <div className="border rounded p-3 mb-3 bg-light">
            <h6 className="text-muted mb-3">Fit note</h6>
            <Row>
              <DetailField label="Status">
                {request.fitNoteStatus ? (
                  <Badge color="info" className="text-capitalize">
                    {FIT_NOTE_STATUS_LABELS[request.fitNoteStatus] ||
                      request.fitNoteStatus}
                  </Badge>
                ) : (
                  "—"
                )}
              </DetailField>
              <DetailField label="Valid from">
                {request.fitNoteValidFrom
                  ? dayjs(request.fitNoteValidFrom).format("DD MMM YYYY")
                  : "—"}
              </DetailField>
              <DetailField label="Valid to">
                {request.fitNoteValidTo
                  ? dayjs(request.fitNoteValidTo).format("DD MMM YYYY")
                  : "—"}
              </DetailField>
            </Row>
            {fitNoteUrl && (
              <a
                href={fitNoteUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-sm btn-outline-primary"
              >
                <i className="mdi mdi-file-document-outline me-1" />
                View fit note
              </a>
            )}
          </div>
        )}

        {request.rejectionReason && (
          <div className="border rounded p-3 mb-3 border-danger">
            <p className="text-muted small mb-1">Rejection reason</p>
            <p className="mb-0">{request.rejectionReason}</p>
          </div>
        )}

        {rejectMode && (
          <div className="border rounded p-3 border-danger">
            <h6 className="mb-3">Reject leave request</h6>
            <div className="mb-3">
              <Label htmlFor="leaveRejectionReason">Rejection reason</Label>
              <Input
                type="textarea"
                id="leaveRejectionReason"
                rows={3}
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Explain why this request is rejected..."
              />
            </div>
            {request.fitNote && (
              <div className="mb-0">
                <Label htmlFor="leaveFitNoteStatus">Fit note status</Label>
                <Input
                  type="select"
                  id="leaveFitNoteStatus"
                  value={fitNoteStatus}
                  onChange={(e) => setFitNoteStatus(e.target.value)}
                >
                  <option value="">Select fit note issue</option>
                  <option value={ADMIN_FIT_NOTE_REJECTION_STATUS.MISSING}>
                    {ADMIN_FIT_NOTE_REJECTION_STATUS_LABELS.missing}
                  </option>
                  <option value={ADMIN_FIT_NOTE_REJECTION_STATUS.EXPIRED}>
                    {ADMIN_FIT_NOTE_REJECTION_STATUS_LABELS.expired}
                  </option>
                </Input>
              </div>
            )}
          </div>
        )}
      </ModalBody>
      <ModalFooter className="d-flex flex-wrap gap-2 justify-content-between">
        <Button color="secondary" onClick={toggle} disabled={isSubmitting}>
          Close
        </Button>

        {canDecide && !rejectMode && (
          <div className="d-flex gap-2">
            <Button color="success" onClick={handleApprove} disabled={isSubmitting}>
              {isSubmitting ? "Submitting..." : "Approve"}
            </Button>
            <Button
              color="danger"
              outline
              onClick={() => setRejectMode(true)}
              disabled={isSubmitting}
            >
              Reject
            </Button>
          </div>
        )}

        {canDecide && rejectMode && (
          <div className="d-flex gap-2">
            <Button
              color="secondary"
              outline
              onClick={() => setRejectMode(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button color="danger" onClick={handleConfirmReject} disabled={isSubmitting}>
              {isSubmitting ? "Submitting..." : "Confirm reject"}
            </Button>
          </div>
        )}
      </ModalFooter>
    </Modal>
  );
}

LeaveRequestDetailModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  request: PropTypes.object,
  onApprove: PropTypes.func,
  onReject: PropTypes.func,
  isSubmitting: PropTypes.bool,
};
